import { Box, Typography } from '@mui/material';
import { useChildTokens } from './useChildTokens';
import { formatTime, remainingFraction, TimerViewProps, TIMER_SURFACE } from './timerView';

/**
 * Urtavlan med VISARE — en analog klocka där visaren vandrar tillbaka mot 12.
 *
 * Visaren pekar på hur mycket som är KVAR, inte på klockslaget: full tid = ett varv,
 * ingen tid = rakt upp. Den rör sig alltså moturs, samma riktning som tårtbitens kant
 * (TimerWedge) — de två formerna visar samma sak, den ena med massa och den andra med
 * en linje. För den som läser analog klocka är visaren det snabbaste sättet att se
 * "ungefär en fjärdedel kvar" utan siffror alls.
 *
 * Ren renderare: tiden kommer som prop, ingen egen setInterval (se timerView.ts).
 */

const TICK_COUNT = 12;
/** Knappens ram — absolut positionerade barn utgår från padding-boxen. */
const FACE_BORDER = 2;
const TICK_INSET = 3;
const FACE_RADIUS = TIMER_SURFACE / 2;
const TICK_ORIGIN_Y = FACE_RADIUS - FACE_BORDER - TICK_INSET;
/** Visarens längd från centrum. Kortare än radien så spetsen inte rör timstrecken. */
const HAND_LENGTH = FACE_RADIUS - 26;

const TimerDial = ({
  time,
  totalSeconds,
  label,
  color,
  onToggle,
  showDigits = true,
}: TimerViewProps) => {
  const tokens = useChildTokens();
  const degrees = remainingFraction(time, totalSeconds) * 360;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2.5 }}>
      <Box
        component="button"
        type="button"
        data-testid="timer-dial"
        onClick={onToggle}
        aria-label={label}
        sx={{
          width: TIMER_SURFACE,
          height: TIMER_SURFACE,
          borderRadius: '50%',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          p: 0,
          bgcolor: tokens.card,
          border: `${FACE_BORDER}px solid`,
          borderColor: tokens.border,
          fontFamily: 'inherit',
          overflow: 'hidden',
          cursor: onToggle ? 'pointer' : 'default',
        }}
      >
        {/* Timstreck — gör tavlan läsbar som en klocka utan att röra sig. */}
        {Array.from({ length: TICK_COUNT }, (_unused, i) => {
          const isQuarter = i % 3 === 0;
          return (
            <Box
              key={i}
              aria-hidden
              sx={{
                position: 'absolute',
                top: TICK_INSET,
                left: '50%',
                width: isQuarter ? 3 : 2,
                height: isQuarter ? 13 : 8,
                ml: isQuarter ? '-1.5px' : '-1px',
                borderRadius: '2px',
                bgcolor: isQuarter ? tokens.text : tokens.textSoft,
                transformOrigin: `50% ${TICK_ORIGIN_Y}px`,
                transform: `rotate(${i * (360 / TICK_COUNT)}deg)`,
              }}
            />
          );
        })}

        {/* Visaren. transformOrigin ligger i NEDRE änden så att staven roterar kring
            tavlans mitt; rotationen är andelen kvar, alltså moturs mot 12. */}
        <Box
          data-testid="timer-dial-hand"
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: '50%',
            left: '50%',
            width: 4,
            height: HAND_LENGTH,
            ml: '-2px',
            borderRadius: '2px',
            bgcolor: color,
            transformOrigin: '50% 100%',
            transform: `rotate(${degrees}deg)`,
            transition: 'transform 1s linear',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
        {/* Navet döljer visarens fäste. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: color,
          }}
        />

        {/* Siffror och etikett sitter i NEDRE halvan, utanför navet, så att visaren
            aldrig skriver över dem. */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 18,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {showDigits && (
            <Typography
              sx={{
                fontSize: '1.3rem',
                fontWeight: 900,
                color: tokens.text,
                bgcolor: tokens.card,
                borderRadius: '8px',
                px: 0.75,
              }}
            >
              {formatTime(time)}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              color: tokens.textSoft,
              bgcolor: tokens.card,
              borderRadius: '6px',
              px: 0.5,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TimerDial;
